import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Chip,
  Pagination,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Stack,
  Fab,
  useTheme,
  useMediaQuery,
  Collapse,
  InputAdornment,
  Divider,
  Drawer
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Category as CategoryIcon,
  FilterList as FilterIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  Close as CloseIcon,
  Clear as ClearIcon
} from '@mui/icons-material';

const BASE_URL = 'https://dhahabjw.com';

// Area Card Component
const AreaCard = ({ area, onEdit, onDelete }) => {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {area.name?.en || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} dir="rtl">
              {area.name?.ar || 'N/A'}
            </Typography>
          </Box>
          <Chip
            label={area.isActive ? 'Active' : 'Inactive'}
            size="small"
            color={area.isActive ? 'success' : 'default'}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Shipping: {area.shipping || '0'}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {area.createdAt ? new Date(area.createdAt).toLocaleDateString() : 'N/A'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={() => onEdit(area)}
              size="small"
              color="primary"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => onDelete(area)}
              size="small"
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Area Form Component
const AreaForm = ({ open, onClose, area, onSave, loading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [formData, setFormData] = useState({
    name: { en: '', ar: '' },
    shipping: 0,
    isActive: true
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (area) {
      setFormData({
        name: area.name,
        shipping: area.shipping,
        isActive: area.isActive
      });
    } else {
      setFormData({
        name: { en: '', ar: '' },
        shipping: 0,
        isActive: true
      });
    }
    setErrors({});
  }, [area, open]);

  const handleInputChange = (field, subfield, value) => {
    if (subfield) {
      setFormData(prev => ({
        ...prev,
        [field]: {
          ...prev[field],
          [subfield]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.en?.trim()) newErrors['name.en'] = 'English name is required';
    if (!formData.name?.ar?.trim()) newErrors['name.ar'] = 'Arabic name is required';
    if (formData.shipping < 0) newErrors['shipping'] = 'Shipping cost cannot be negative';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    onSave(formData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { borderRadius: isMobile ? 0 : 2 }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {area ? 'Edit Area' : 'Add New Area'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Area Information
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Area Name (English)"
                value={formData.name.en}
                onChange={(e) => handleInputChange('name', 'en', e.target.value)}
                error={!!errors['name.en']}
                helperText={errors['name.en']}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Area Name (Arabic)"
                value={formData.name.ar}
                onChange={(e) => handleInputChange('name', 'ar', e.target.value)}
                error={!!errors['name.ar']}
                helperText={errors['name.ar']}
                size="small"
                dir="rtl"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Shipping Cost"
                type="number"
                value={formData.shipping}
                onChange={(e) => handleInputChange('shipping', null, e.target.value)}
                error={!!errors['shipping']}
                helperText={errors['shipping']}
                size="small"
                InputProps={{
                  inputProps: { min: 0 }
                }}
              />
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', null, e.target.checked)}
              />
            }
            label="Area Active"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Saving...' : area ? 'Update Area' : 'Create Area'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Area Management Component
const Area = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [viewMode, setViewMode] = useState(isMobile ? 'card' : 'table');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    isActive: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedArea, setSelectedArea] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);

  useEffect(() => {
    setViewMode(isMobile ? 'card' : 'table');
  }, [isMobile]);

  const fetchAreas = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        limit: 20,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        ...(filters.search && { search: filters.search }),
        ...(filters.isActive && { isActive: filters.isActive })
      });

      const response = await fetch(`${BASE_URL}/api/admin/area?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAreas(data.data || []);
        setTotalPages(data.pagination?.pages || 1);
        setTotal(data.pagination?.total || 0);
        setError('');
      } else {
        throw new Error('Failed to fetch areas');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveArea = async (areaData) => {
    setSaveLoading(true);
    try {
      const url = selectedArea
        ? `${BASE_URL}/api/admin/area/${selectedArea._id}`
        : `${BASE_URL}/api/admin/area`;
      const method = selectedArea ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(areaData)
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(selectedArea ? 'Area updated successfully' : 'Area created successfully');
        setFormOpen(false);
        setSelectedArea(null);
        fetchAreas();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save area');
      }
    } catch (err) {
      setError(err.message || 'Network error occurred');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteArea = async () => {
    if (!areaToDelete) return;

    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/api/admin/area/${areaToDelete._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess('Area deleted successfully');
        setDeleteDialogOpen(false);
        setAreaToDelete(null);
        fetchAreas();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete area');
      }
    } catch (err) {
      setError(err.message || 'Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      isActive: '',
      sortBy: 'createdAt',
      sortOrder: 'desc'
    });
    setPage(1);
  };

  useEffect(() => {
    fetchAreas();
  }, [page, filters]);

  const hasActiveFilters = Object.values(filters).some(value => value !== '' && value !== 'createdAt' && value !== 'desc');

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
        gap: 2,
        mb: 3
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Area Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your product areas with multilingual support
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {!isSmallMobile && (
            <Box sx={{ display: 'flex', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <IconButton
                onClick={() => setViewMode('table')}
                color={viewMode === 'table' ? 'primary' : 'default'}
                sx={{ borderRadius: 0 }}
              >
                <ViewListIcon />
              </IconButton>
              <IconButton
                onClick={() => setViewMode('card')}
                color={viewMode === 'card' ? 'primary' : 'default'}
                sx={{ borderRadius: 0 }}
              >
                <ViewModuleIcon />
              </IconButton>
            </Box>
          )}

          <Button
            variant="contained"
            onClick={() => {
              setSelectedArea(null);
              setFormOpen(true);
            }}
            startIcon={<AddIcon />}
          >
            Add Area
          </Button>
        </Box>
      </Box>

      {/* Alerts */}
      <Collapse in={!!error}>
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Collapse>

      <Collapse in={!!success}>
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      </Collapse>

      {/* Mobile Filters Drawer */}
      {isSmallMobile && (
        <Drawer
          anchor="bottom"
          open={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          PaperProps={{
            sx: { borderRadius: '16px 16px 0 0' }
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6">Filters</Typography>
              <IconButton onClick={() => setFiltersOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Search areas..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
                }}
              />

              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.isActive}
                  onChange={(e) => handleFilterChange('isActive', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="createdAt">Creation Date</MenuItem>
                  <MenuItem value="name.en">Name (English)</MenuItem>
                  <MenuItem value="name.ar">Name (Arabic)</MenuItem>
                  <MenuItem value="updatedAt">Last Updated</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Sort Order</InputLabel>
                <Select
                  value={filters.sortOrder}
                  onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                  label="Sort Order"
                >
                  <MenuItem value="asc">Ascending</MenuItem>
                  <MenuItem value="desc">Descending</MenuItem>
                </Select>
              </FormControl>

              {hasActiveFilters && (
                <Button variant="outlined" onClick={handleClearFilters} startIcon={<ClearIcon />}>
                  Clear All Filters
                </Button>
              )}
            </Stack>
          </Box>
        </Drawer>
      )}

      {/* Desktop Filters */}
      {!isSmallMobile && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterIcon /> Filters
            </Typography>
            {hasActiveFilters && (
              <Button variant="outlined" onClick={handleClearFilters} size="small" startIcon={<ClearIcon />}>
                Clear All
              </Button>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search areas..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.isActive}
                  onChange={(e) => handleFilterChange('isActive', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={filters.sortBy}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="createdAt">Creation Date</MenuItem>
                  <MenuItem value="name.en">Name (English)</MenuItem>
                  <MenuItem value="name.ar">Name (Arabic)</MenuItem>
                  <MenuItem value="updatedAt">Last Updated</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort Order</InputLabel>
                <Select
                  value={filters.sortOrder}
                  onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                  label="Sort Order"
                >
                  <MenuItem value="asc">Ascending</MenuItem>
                  <MenuItem value="desc">Descending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Areas Content */}
      <Paper>
        <Box sx={{ p: 3, pb: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Areas ({total})</Typography>
          {isSmallMobile && (
            <Button
              variant="outlined"
              onClick={() => setFiltersOpen(true)}
              startIcon={<FilterIcon />}
              size="small"
            >
              Filters
            </Button>
          )}
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {viewMode === 'table' && !isMobile ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name (English)</TableCell>
                      <TableCell>Name (Arabic)</TableCell>
                      <TableCell>Shipping</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {areas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                            No areas found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {hasActiveFilters ? 'Try adjusting your filters' : 'Get started by adding your first area'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      areas.map((area) => (
                        <TableRow key={area._id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <CategoryIcon sx={{ mr: 1, color: 'primary.main' }} fontSize="small" />
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {area.name?.en || 'N/A'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" dir="rtl">
                              {area.name?.ar || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {area.shipping || '0'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={area.isActive ? 'Active' : 'Inactive'}
                              size="small"
                              color={area.isActive ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                onClick={() => {
                                  setSelectedArea(area);
                                  setFormOpen(true);
                                }}
                                size="small"
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                onClick={() => {
                                  setAreaToDelete(area);
                                  setDeleteDialogOpen(true);
                                }}
                                size="small"
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 3 }}>
                {areas.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                      No areas found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters ? 'Try adjusting your filters' : 'Get started by adding your first area'}
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {areas.map((area) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={area._id}>
                        <AreaCard
                          area={area}
                          onEdit={(area) => {
                            setSelectedArea(area);
                            setFormOpen(true);
                          }}
                          onDelete={(area) => {
                            setAreaToDelete(area);
                            setDeleteDialogOpen(true);
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Floating Action Button for Mobile */}
      {isSmallMobile && (
        <Fab
          color="primary"
          onClick={() => {
            setSelectedArea(null);
            setFormOpen(true);
          }}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Area Form Dialog */}
      <AreaForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setSelectedArea(null);
        }}
        area={selectedArea}
        onSave={handleSaveArea}
        loading={saveLoading}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "<strong>{areaToDelete?.name?.en || areaToDelete?.name?.ar}</strong>"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone and may affect associated products.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteArea}
            color="error"
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {loading ? 'Deleting...' : 'Delete Area'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Area;